import { NgTemplateOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core'
import { FormsModule } from '@angular/forms'

import { TranslatePipe } from '@ngx-translate/core'
import { isEqual } from 'lodash-es'
import { Button } from 'primeng/button'
import { IconField } from 'primeng/iconfield'
import { InputIcon } from 'primeng/inputicon'
import { InputText } from 'primeng/inputtext'

import { CloseIconComponent } from '@ti/core/icons/close'
import { SearchIconComponent } from '@ti/core/icons/search'

import { BottomDialogComponent } from '../../layout/components/bottom-dialog/bottom-dialog.component'
import { scrollToCenter } from '../../layout/utils'

export type AutocompleteInputMode = 'text' | 'email'

export interface AutocompleteOptionContext<O> {
  $implicit: O
}

type SheetStatus = 'idle' | 'loading' | 'results' | 'empty'

// Both mirror PrimeNG AutoComplete, which reads `label`/`value` when the option has them
export function autocompleteOptionLabel(option: unknown): string {
  return String(optionField(option, 'label') ?? option ?? '')
}

export function autocompleteOptionValue(option: unknown): unknown {
  return optionField(option, 'value') ?? option
}

function optionField(option: unknown, field: string): unknown {
  return option !== null && typeof option === 'object'
    ? (option as Record<string, unknown>)[field]
    : undefined
}

/** The phone search sheet of an autocomplete; the parent searches and owns the results. */
@Component({
  selector: 'app-autocomplete-sheet',
  templateUrl: 'autocomplete-sheet.component.html',
  styleUrls: ['autocomplete-sheet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BottomDialogComponent,
    Button,
    CloseIconComponent,
    FormsModule,
    IconField,
    InputIcon,
    InputText,
    NgTemplateOutlet,
    SearchIconComponent,
    TranslatePipe,
  ],
})
export class AutocompleteSheetComponent<O = unknown> {
  public readonly suggestions = input<O[]>([])
  /** The option field shown as its label; without it the sheet mirrors PrimeNG's fallbacks. */
  public readonly optionLabel = input<string>()
  /** The parent's search for the latest query is still running. */
  public readonly loading = input<boolean>(false)
  /** Apply commits the typed text; otherwise a suggestion has to be picked. */
  public readonly freeText = input<boolean>(false)
  /** With free text, keeps Apply off until the typed text is a value the parent accepts. */
  public readonly canCommitText = input<boolean>(true)
  /** Searches as soon as the sheet opens and whenever the query is cleared. */
  public readonly completeOnFocus = input<boolean>(false)
  public readonly delay = input<number>(300)
  public readonly minLength = input<number>(1)
  public readonly heading = input<string>('')
  public readonly placeholder = input<string>()
  public readonly inputMode = input<AutocompleteInputMode>('text')
  public readonly applyLabel = input<string>()
  /** Off keeps the keyboard down, so a browsable list stays visible until the search is tapped. */
  public readonly focusOnShow = input<boolean>(true)

  public readonly searchChange = output<string>()
  public readonly optionSelect = output<O>()
  /** A suggestion is marked; a custom `#input` shows it, since the sheet only writes its own field. */
  public readonly optionTap = output<O>()
  public readonly textCommit = output<string>()
  public readonly closed = output<void>()

  protected readonly itemTemplate = contentChild<TemplateRef<AutocompleteOptionContext<O>>>('item')
  protected readonly headerTemplate = contentChild<TemplateRef<void>>('header')
  protected readonly emptyTemplate = contentChild<TemplateRef<void>>('empty')
  /** Replaces the search field; it reports its text through `setQuery` and Enter through `submit`. */
  protected readonly inputTemplate = contentChild<TemplateRef<void>>('input')

  private readonly header = viewChild<ElementRef<HTMLElement>>('header')

  private readonly scrollWrapper = viewChild<ElementRef<HTMLElement>>('scrollWrapper')

  protected readonly visible = signal(false)
  protected readonly query = signal('')
  protected readonly selectedOption = signal<O | undefined>(undefined)
  /** Results stay hidden until this opening has searched, so rows of an earlier query never show. */
  protected readonly searched = signal(false)

  protected readonly status = computed<SheetStatus>(() => {
    if (!this.searched()) return 'idle'
    if (this.loading()) return 'loading'
    return this.suggestions().length ? 'results' : 'empty'
  })

  protected readonly selectedIndex = computed(() => {
    const selected = this.selectedOption()
    if (selected === undefined) return -1

    const value = autocompleteOptionValue(selected)
    return this.suggestions().findIndex((option) => isEqual(autocompleteOptionValue(option), value))
  })

  protected readonly canApply = computed(
    () => this.selectedOption() !== undefined || (this.freeText() && this.canCommitText()),
  )

  private searchTimer?: ReturnType<typeof setTimeout>
  private scrollToSelected = false

  public constructor() {
    inject(DestroyRef).onDestroy(() => this.clearSearchTimer())
  }

  /** `selected` marks the current option, so Apply works without a new pick. */
  public openMobilePanel(query = '', selected?: O) {
    this.clearSearchTimer()
    this.query.set(query)
    this.selectedOption.set(selected)
    this.scrollToSelected = selected !== undefined
    this.searched.set(false)
    this.visible.set(true)

    if (this.completeOnFocus()) {
      this.search(query)
    }
  }

  public applyValue() {
    if (!this.canApply()) return

    const selected = this.selectedOption()
    this.close()

    if (selected !== undefined) {
      this.optionSelect.emit(selected)
    } else {
      // Phone keyboards append spaces that would fail validation such as Validators.email
      this.textCommit.emit(this.query().trim())
    }
  }

  public cancelValue() {
    this.close()
  }

  public setQuery(query: string) {
    this.onQueryChange(query)
  }

  public submit() {
    if (this.canApply()) {
      this.applyValue()
      return
    }

    const query = this.query()
    if (query.trim().length >= this.minLength()) {
      this.clearSearchTimer()
      this.search(query)
    }
    this.headerInput()?.blur()
  }

  protected onQueryChange(query: string) {
    this.query.set(query)
    this.scrollToSelected = false

    const selected = this.selectedOption()
    if (selected !== undefined && query !== this.getOptionLabel(selected)) {
      this.selectedOption.set(undefined)
    }

    this.clearSearchTimer()

    if (query.trim().length < this.minLength()) {
      if (!query && this.completeOnFocus()) {
        this.search('')
      } else {
        this.searched.set(false)
      }
      return
    }

    const delay = this.delay()
    if (delay > 0) {
      this.searchTimer = setTimeout(() => this.search(query), delay)
    } else {
      this.search(query)
    }
  }

  protected tapOption(option: O) {
    this.clearSearchTimer()
    this.scrollToSelected = false
    this.selectedOption.set(option)
    this.query.set(this.getOptionLabel(option))
    this.optionTap.emit(option)
    // Drops the keyboard so the footer buttons are reachable
    this.headerInput()?.blur()
  }

  protected onEnter(event: Event) {
    event.preventDefault()
    this.submit()
  }

  protected clearQuery(searchInput: HTMLInputElement) {
    this.onQueryChange('')
    searchInput.focus()
  }

  protected onPlaced() {
    if (!this.scrollToSelected) return

    this.scrollToSelected = false
    const el = this.scrollWrapper()?.nativeElement
    const selected = el?.querySelector('.option-item.selected')
    if (el && selected) scrollToCenter(el, selected)
  }

  // A free-text sheet is a value field, so its keyboard opens once the open animation has ended
  protected onShown() {
    if (this.freeText()) {
      this.headerInput()?.focus()
    }
  }

  protected onVisibleChange(visible: boolean) {
    if (!visible) {
      this.cancelValue()
    }
  }

  protected getOptionLabel(option: O): string {
    const field = this.optionLabel()
    return field ? String(optionField(option, field) ?? '') : autocompleteOptionLabel(option)
  }

  private headerInput() {
    return this.header()?.nativeElement.querySelector('input')
  }

  private search(query: string) {
    this.searched.set(true)
    this.searchChange.emit(query)
  }

  private close() {
    this.clearSearchTimer()
    if (!this.visible()) return

    this.visible.set(false)
    this.closed.emit()
  }

  private clearSearchTimer() {
    clearTimeout(this.searchTimer)
    this.searchTimer = undefined
  }
}
